import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { INVOICE_ONBOARDING_HREF } from '../components/InvoiceOnboarding'

/**
 * Static help pages for the portal. Plain text, no CMS. Every statement here
 * describes what the app does today -- check it against the code before
 * adding to it.
 */
export interface HelpTopic {
  slug: string
  title: string
  summary: string
  // Extra words for Ctrl+K.
  keywords: string
  body: ReactNode
}

const L = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="font-medium text-brand-700 underline dark:text-brand-400">
    {children}
  </Link>
)

export const HELP_TOPICS: HelpTopic[] = [
  {
    slug: 'getting-started',
    title: 'Getting started',
    summary: 'The steps to get your account ready.',
    keywords: 'setup checklist begin first steps start sending',
    body: (
      <>
        <p>
          The setup list on your <L to="/dashboard">Dashboard</L> shows what is left. It stays
          until every step is done.
        </p>
        <ol>
          <li>Answer the WhatsApp consent question.</li>
          <li>Set your payment terms.</li>
          <li>Fill in your customer contact page.</li>
          <li>Load your invoices, from your accounting system or a file.</li>
          <li>Clear any contact issues, so every customer can be reached.</li>
          <li>Press Start to begin sending.</li>
        </ol>
        <p>
          A new account sends nothing by itself. When you are ready, the Dashboard shows the
          messages that will go out on the next working day. Nothing is sent until you press
          Start. The owner or a manager can press it.
        </p>
        <p>A customer gets at most one automatic message a day.</p>
      </>
    ),
  },
  {
    slug: 'importing-invoices',
    title: 'Importing invoices',
    summary: 'Bring invoices in from Excel or CSV, or connect your accounting system.',
    keywords: 'import excel csv upload spreadsheet xero quickbooks sage',
    body: (
      <>
        <h2>From a file</h2>
        <ol>
          <li>
            Open <L to="/reminders">Reminders</L> and choose Import reminders.
          </li>
          <li>Download the Excel file. Fill in the Data sheet. Read the Instructions sheet first.</li>
          <li>Select the box to say you may share these customers&apos; details with RemindLoop.</li>
          <li>Add your file. A CSV file works too.</li>
          <li>
            We check the file first and show what will be imported. Nothing is saved until you
            confirm.
          </li>
        </ol>
        <p>
          A file can be up to 10 MB and 10,000 rows. Invoices you already imported are skipped,
          not added twice.
        </p>
        <h2>From your accounting system</h2>
        <p>
          Connect Xero, QuickBooks or Sage on the <L to="/connections">Accounting connections</L>{' '}
          page. Invoices then come in by themselves. Before each reminder we check your
          accounting system. If the invoice is already paid, nothing is sent and it is marked paid.
        </p>
        <p>
          A file has no accounting system behind it, so we cannot check it. Reminders for those
          invoices continue until you mark them paid.
        </p>
      </>
    ),
  },
  {
    slug: 'invoice-onboarding',
    title: 'Invoice onboarding',
    summary: 'What happens when an invoice is loaded.',
    keywords: 'introduction overdue weekend holiday final notice daily limit first message',
    body: (
      <>
        <p>
          An invoice that is already overdue when it is loaded gets one introduction first. Other
          rules cover weekends, holidays and the final notice.
        </p>
        <p>
          The full explanation, with a diagram, is on the <L to={INVOICE_ONBOARDING_HREF}>Reminder rules</L>{' '}
          page.
        </p>
      </>
    ),
  },
  {
    slug: 'stopping-and-closing',
    title: 'Stopping and closing reminders',
    summary: 'Stop one invoice, or stop everything.',
    keywords: 'cancel mark paid reopen undo pause resume stop all sending opt out',
    body: (
      <>
        <h2>One invoice</h2>
        <p>
          Open the invoice on the <L to="/reminders">Reminders</L> page. Choose Mark paid, or
          Cancel invoice. You can add a note. No more messages go out for it. Undo or Reopen
          puts it back.
        </p>
        <p>
          Invoices that come from your accounting system are changed there. Mark paid and Cancel
          invoice still work here.
        </p>
        <h2>Everything at once</h2>
        <p>
          Stop all sending is on the Reminders page and in <L to="/settings">Settings</L>. The
          owner or a manager can use it. No customer gets a message until you choose Resume
          sending. Messages that were planned do not go out.
        </p>
        <h2>When a customer asks to stop</h2>
        <p>
          If a customer replies STOP on WhatsApp or SMS, we stop messaging that phone number. You
          can see these on the <L to="/outstanding-contacts">Contact issues</L> page.
        </p>
        <h2>When a customer says they paid</h2>
        <p>
          A customer can tap an I&apos;ve paid button. We pause reminders for that invoice for a
          few working days, and the Dashboard asks you to confirm or reject it.
        </p>
      </>
    ),
  },
  {
    slug: 'whatsapp-consent',
    title: 'WhatsApp consent',
    summary: 'Whether your customers agreed to WhatsApp messages.',
    keywords: 'whatsapp permission opt in sms confirm legal popia',
    body: (
      <>
        <p>
          This is general information. It is not legal advice. Ask your own adviser if you are
          unsure.
        </p>
        <p>
          Permission means the customer agreed to get messages from you on WhatsApp. For example:
          in your terms, on your invoice, on a signed form, or because they messaged you on
          WhatsApp first. Having their phone number is not enough.
        </p>
        <p>You choose one of two ways, in Settings under Reminders:</p>
        <ul>
          <li>
            <strong>Yes, they agreed.</strong> You confirm it with one tap. Reminders start on
            WhatsApp. RemindLoop cannot check your answer. You are responsible for it.
          </li>
          <li>
            <strong>Ask them first.</strong> Each customer gets one SMS with a confirm link. They
            get SMS until they confirm.
          </li>
        </ul>
        <p>
          If a number is not on WhatsApp, or the message does not arrive, we send an SMS instead.
          You can change your answer any time.
        </p>
        <p>
          <L to="/settings?tab=reminders#whatsapp-consent">Go to WhatsApp consent</L>
        </p>
      </>
    ),
  },
  {
    slug: 'customer-contact-page',
    title: 'Customer contact page',
    summary: 'The page customers see when an invoice has no link.',
    keywords: 'contact page payment instructions link hosted business details',
    body: (
      <>
        <p>
          When your accounting system gives no invoice link, the message links to a page we host
          for you. It shows your business name, your contact details, your payment instructions
          and the invoice reference. It never shows the customer&apos;s name, phone number or amount.
        </p>
        <p>
          The owner or a manager fills it in under <L to="/settings?tab=business">Settings, Business</L>.
        </p>
      </>
    ),
  },
  {
    slug: 'alerts',
    title: 'Alerts',
    summary: 'An email to the owner when something needs you.',
    keywords: 'email notify owner summary monday message',
    body: (
      <>
        <p>
          Alerts are off until you turn them on. Then every owner gets an email, at the address
          they log in with, when something needs you: at most one a day, on working days in the
          morning. You also get a short summary every Monday, instead of that day's alert.
        </p>
        <p>
          The email has counts only. It never has a customer name, an amount or a phone number.
          Every email has a link to turn alerts off.
        </p>
        <p>
          <L to="/settings?tab=reminders">Go to Alerts in Settings</L>
        </p>
      </>
    ),
  },
  {
    slug: 'plan-and-billing',
    title: 'Plan and billing',
    summary: 'Your plan, your usage and your payment card.',
    keywords: 'subscription price allowance usage card cancel invoice annual monthly tier upgrade',
    body: (
      <>
        <p>
          The owner and managers can open <L to="/settings?tab=billing">Settings, Billing</L>. It
          shows your plan, your payment status, how you pay and what you used this
          month, by WhatsApp and SMS.
        </p>
        <p>
          When you are close to your plan allowance, or over it, the Dashboard tells you. You can
          update your card or cancel from the same page.
        </p>
        <p>To compare plans, see the <a className="font-medium text-brand-700 underline dark:text-brand-400" href="/pricing">pricing page</a>.</p>
      </>
    ),
  },
]

export function findHelpTopic(slug: string | undefined): HelpTopic | undefined {
  return HELP_TOPICS.find((t) => t.slug === slug)
}
